import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { ArrowsOutCardinal, X, ArrowCounterClockwise } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { dmsApi } from './dmsApi';

export default function PageReorderDialog({ doc, onClose, onDone }) {
  const [pages, setPages] = useState(null);   // [{idx, thumb}]
  const [busy, setBusy] = useState(false);
  const [total, setTotal] = useState(0);
  const dragFrom = useRef(null);

  useEffect(() => {
    if (!doc) return;
    setPages(null);
    dmsApi.pageThumbs(doc.id)
      .then((r) => {
        setTotal(r.total_pages);
        setPages(r.thumbs.map((thumb, idx) => ({ idx, thumb })));
        if (r.truncated) toast.info('先頭60ページのみ表示しています');
      })
      .catch((e) => { toast.error(e?.response?.data?.detail || 'ページの読み込みに失敗しました'); onClose(); });
  }, [doc, onClose]);

  if (!doc) return null;

  const move = (from, to) => setPages((ps) => {
    const next = [...ps];
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    return next;
  });
  const removeAt = (i) => setPages((ps) => {
    if (ps.length <= 1) { toast.error('最後の1ページは削除できません'); return ps; }
    return ps.filter((_, j) => j !== i);
  });
  const reset = () => {
    setPages(null);
    dmsApi.pageThumbs(doc.id)
      .then((r) => { setTotal(r.total_pages); setPages(r.thumbs.map((thumb, idx) => ({ idx, thumb }))); })
      .catch(() => toast.error('再読み込みに失敗しました'));
  };

  const changed = pages && (pages.length !== total || pages.some((p, i) => p.idx !== i));

  const save = async () => {
    if (!changed) { toast.error('変更がありません'); return; }
    setBusy(true);
    try {
      const r = await dmsApi.reorderPages(doc.id, pages.map((p) => p.idx));
      toast.success(`新しい版（v${r.version_number}）として保存しました${r.removed_pages ? `（${r.removed_pages}ページ削除）` : ''} — 旧版は履歴に残ります`);
      onDone(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || '保存に失敗しました'); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" aria-describedby={undefined} data-testid="page-reorder-dialog">
        <DialogHeader>
          <DialogTitle className="text-base flex items-center gap-2">
            <ArrowsOutCardinal size={17} className="text-orange-600" /> ページ並替 — {doc.title}
          </DialogTitle>
        </DialogHeader>
        <p className="text-[11px] text-zinc-500">ドラッグで並び替え、×で削除できます。保存すると新しい版が作成され、元の版は履歴に残ります。</p>
        {!pages ? (
          <p className="py-10 text-center text-sm text-zinc-400">ページを読み込み中…</p>
        ) : (
          <div className="grid grid-cols-4 sm:grid-cols-5 gap-2 max-h-[52vh] overflow-y-auto p-1" data-testid="page-reorder-grid">
            {pages.map((p, i) => (
              <div key={p.idx} draggable
                onDragStart={() => { dragFrom.current = i; }}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => { e.preventDefault(); if (dragFrom.current != null && dragFrom.current !== i) move(dragFrom.current, i); dragFrom.current = null; }}
                className="relative rounded-lg border-2 border-zinc-200 bg-white overflow-hidden cursor-grab active:cursor-grabbing hover:border-orange-400 transition-colors group"
                data-testid={`page-tile-${p.idx}`}>
                <img src={p.thumb} alt={`ページ${p.idx + 1}`} className="w-full aspect-[3/4] object-contain bg-zinc-50 pointer-events-none" />
                <span className="absolute top-1 left-1 w-5 h-5 rounded-full bg-orange-600 text-white text-[10px] font-black flex items-center justify-center">{i + 1}</span>
                <span className="absolute bottom-1 left-1 text-[8px] text-zinc-400 bg-white/80 rounded px-1">元p.{p.idx + 1}</span>
                <button type="button" onClick={() => removeAt(i)}
                  className="absolute top-1 right-1 w-5 h-5 rounded-full bg-white/90 border border-zinc-200 text-zinc-400 hover:text-rose-600 hover:border-rose-300 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  data-testid={`page-remove-${p.idx}`}>
                  <X size={11} weight="bold" />
                </button>
              </div>
            ))}
          </div>
        )}
        <DialogFooter className="items-center">
          {pages && (
            <span className="text-[10px] text-zinc-400 mr-auto tabular-nums">
              {pages.length}/{total}ページ{changed ? '（変更あり）' : ''}
            </span>
          )}
          <Button variant="outline" size="sm" onClick={reset} className="h-8 text-xs" data-testid="page-reorder-reset">
            <ArrowCounterClockwise size={13} className="mr-1" /> リセット
          </Button>
          <Button variant="outline" onClick={onClose}>キャンセル</Button>
          <Button onClick={save} disabled={busy || !changed} className="bg-teal-700 hover:bg-teal-800" data-testid="page-reorder-save">
            {busy ? '保存中…' : '新しい版として保存'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
